// Admin-facing REST service for hallpass-backend's user-api (all routes
// mounted at /api/users — apps/user-api/src/app.ts's
// `app.use("/api/users", userRouter)`). See src/api/config.js's
// USER_API_BASE and src/api/http.js's userApi().
//
// Route/role reference (apps/user-api/src/routes/user.ts):
//   GET    /api/users                     TEACHER/ADMIN/SUPER_ADMIN — cursor-paginated; scoped to caller's school unless SUPER_ADMIN
//   POST   /api/users                     ADMIN/SUPER_ADMIN
//   POST   /api/users/bulk                ADMIN/SUPER_ADMIN — max 100 per request (bulkCreateSchema, packages/types/src/schemas.ts)
//   GET    /api/users/:id                 self, or TEACHER/ADMIN/SUPER_ADMIN
//   PATCH  /api/users/:id                 self, or ADMIN/SUPER_ADMIN
//   DELETE /api/users/:id                 ADMIN/SUPER_ADMIN
//   POST   /api/users/:id/resend-invite   ADMIN/SUPER_ADMIN — 409 if the user already set a password
//   POST   /api/users/:id/regenerate-pin  ADMIN/SUPER_ADMIN — 409 if the target is not a STUDENT
//
// createUser/updateUser bodies are validated against createUserSchema /
// updateUserSchema (packages/types/src/schemas.ts):
//   create: {email, name, role?, schoolId?}
//   update: {name?, email?, role?, schoolId?} (at least one field required)
// A non-SUPER_ADMIN caller gets 403 for any request body containing
// `schoolId`, and 403 on PATCH for `email`/`role` changes unless they're
// ADMIN+ — the backend enforces this, no client-side gating is done here.

import { userApi, buildQuery } from './http';
import type { components } from './types.gen';

type UserResponse = components['schemas']['UserResponse'];
type UserCursorPage = components['schemas']['UserCursorPage'];
type CreateUserBody = components['schemas']['CreateUserBody'];
type UpdateUserBody = components['schemas']['UpdateUserBody'];
type BulkUserResult = components['schemas']['BulkUserResult'];

/** Filters for {@link listUsers}. `ids` short-circuits `role`/`q` server-side. */
export type ListUsersParams = {
  role?: string;
  ids?: (string | number)[] | string;
  q?: string;
  cursor?: string;
  limit?: number;
};

const BULK_CHUNK_SIZE = 100;

export function chunkArray<T>(arr: T[], size: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < arr.length; i += size) {
    chunks.push(arr.slice(i, i + size));
  }
  return chunks;
}

/**
 * List users. The server always scopes results to the caller's school
 * unless the caller is SUPER_ADMIN. Passing `ids` short-circuits `role`/`q`
 * server-side (an explicit id lookup ignores the other filters), same as
 * the backend's own precedence.
 *
 * @param params - optional list filters
 * @returns the cursor page of users
 */
export function listUsers(params: ListUsersParams = {}): Promise<UserCursorPage> {
  return userApi<UserCursorPage>(`/api/users${buildQuery(params)}`);
}

/**
 * Create a single user. Sends an invite (set-password) email server-side;
 * an email failure after the row is committed does not fail this call
 * (the backend logs and continues — see resendInvite for the recovery
 * path).
 *
 * @param body - the new user's fields
 * @returns the created user
 * @throws {import('./http').ApiError} 409 "Email already in use"; 403 Forbidden (role/schoolId escalation)
 */
export function createUser(body: CreateUserBody): Promise<UserResponse> {
  return userApi<UserResponse>('/api/users', { method: 'POST', body });
}

/**
 * Bulk-create users. Chunks the input into batches of 100 — the backend's
 * bulkCreateSchema max (packages/types/src/schemas.ts) — and sends chunks
 * sequentially: each user provisioning does a slow scrypt hash server-side,
 * and every chunk already fans out internally with its own concurrency pool
 * (apps/user-api/src/routes/user.ts's BULK_CONCURRENCY = 8), so chunks are
 * deliberately not parallelized on top of that. When a chunk request
 * succeeds, its own per-user failures (the backend's `{created, failed}`
 * response body) are aggregated, with `index` remapped from the
 * chunk-relative index the backend returns back to the index in the
 * original `users` array passed in here.
 *
 * If a chunk request itself fails — network failure, or any non-2xx
 * response, including the all-users-failed case where the backend responds
 * 400 instead of 200 (`res.status(failed.length === users.length ? 400 :
 * 200)` in apps/user-api/src/routes/user.ts) — the error propagates out of
 * this function immediately, same as every other function in this API
 * layer. Callers should wrap this call in a try/catch; any chunks already
 * processed before the failing one are not returned on that path.
 *
 * @param users - the users to create
 * @returns the aggregated created count and per-user failures
 * @throws {import('./http').ApiError} on a failed chunk request (network failure or non-2xx response)
 */
export async function createUsersBulk(users: CreateUserBody[]): Promise<BulkUserResult> {
  const chunks = chunkArray(users, BULK_CHUNK_SIZE);
  let created = 0;
  const failed: BulkUserResult['failed'] = [];
  let offset = 0;

  for (const batch of chunks) {
    const batchOffset = offset;
    const result = await userApi<BulkUserResult>('/api/users/bulk', {
      method: 'POST',
      body: batch,
    });
    created += result.created;
    result.failed.forEach((f) => {
      failed.push({
        index: batchOffset + f.index,
        email: f.email,
        error: f.error,
      });
    });
    offset += batch.length;
  }

  return { created, failed };
}

/**
 * Update a user. Self-service is allowed for most fields; changing `email`
 * or `role` requires ADMIN+, and `schoolId` may only be set by SUPER_ADMIN
 * (backend 403s otherwise). Changing `email` revokes the user's existing
 * sessions server-side.
 *
 * @param id - the user's id
 * @param body - the fields to change (at least one required)
 * @returns the updated user
 * @throws {import('./http').ApiError} 409 "Email already in use"; 403 Forbidden
 */
export function updateUser(id: number | string, body: UpdateUserBody): Promise<UserResponse> {
  return userApi<UserResponse>(`/api/users/${id}`, { method: 'PATCH', body });
}

/**
 * Soft-delete a user (ADMIN/SUPER_ADMIN only) and revoke their sessions.
 * 403s if the target's role outranks (or ties) the caller's.
 *
 * @param id - the user's id
 * @returns null
 * @throws {import('./http').ApiError} 403 Forbidden if target outranks the caller
 */
export function deleteUser(id: number | string): Promise<null> {
  return userApi<null>(`/api/users/${id}`, { method: 'DELETE' });
}

/**
 * Re-send a user's invite (set-password) email. Recovery path for a
 * lost/expired invite now that provisioning has no tempPassword fallback.
 *
 * @param id - the user's id
 * @returns null
 * @throws {import('./http').ApiError} 409 if the user already set a password
 */
export function resendInvite(id: number | string): Promise<null> {
  return userApi<null>(`/api/users/${id}/resend-invite`, { method: 'POST' });
}

/**
 * Fetch a single user by id. When an ADMIN/SUPER_ADMIN fetches a STUDENT
 * that isn't themselves, the backend includes the student's `pinCode` (the
 * parent voice-lookup PIN); TEACHER callers and self-views never receive it
 * (apps/user-api/src/routes/user.ts's GET /:id `canViewPin` gate).
 *
 * @param id - the user's id
 * @returns the user (with `pinCode` for an ADMIN+ viewing a student)
 * @throws {import('./http').ApiError} 404 if not found or outside the caller's school scope
 */
export function getUser(id: number | string): Promise<UserResponse> {
  return userApi<UserResponse>(`/api/users/${id}`);
}

/**
 * Regenerate a student's PIN (ADMIN/SUPER_ADMIN only, never self-service).
 * Mints a fresh unique `pinCode`, invalidating the old one, and returns the
 * updated user with the new pin.
 *
 * @param id - the user's id
 * @returns the updated user, including the new `pinCode`
 * @throws {import('./http').ApiError} 409 if the target is not a STUDENT; 404 if outside scope
 */
export function regeneratePin(id: number | string): Promise<UserResponse> {
  return userApi<UserResponse>(`/api/users/${id}/regenerate-pin`, { method: 'POST' });
}
